import classNames from 'classnames';
import {
  CSSProperties,
  ButtonHTMLAttributes,
  ReactNode,
  MouseEventHandler,
  forwardRef,
} from 'react';
import styles from './style.module.scss';
import { TypographyVariant, UITypography } from '../typography/typography';
import { Spinner } from 'components/loading/spinner';

type ButtonVariant = 'primary' | 'secondary' | 'text' | 'gray' | 'green';
type ButtonSize = 'small1' | 'small2' | 'default';
type CommonButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;
export type UiButtonProps = {
  children?: ReactNode | string | number;
  variant?: ButtonVariant;
  fullwidth?: boolean;
  size?: ButtonSize;
  className?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  style?: CSSProperties;
  fontVariant?: TypographyVariant;
  loading?: boolean;
};

export const UIButton = forwardRef<HTMLButtonElement, CommonButtonProps & UiButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      fullwidth,
      size = 'default',
      fontVariant = 'Button1',
      loading,
      onClick,
      ...props
    },
    ref,
  ) => {
    const mergedClasName = classNames(
      styles.button,
      styles[variant] || '',
      fullwidth ? styles.fullwidth : '',
      styles[size] || '',
      className,
    );

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (loading) return;
      onClick?.(e);
    };

    // For primary, gray, and green variants, use new layered structure
    if (variant === 'primary' || variant === 'gray' || variant === 'green') {
      return (
        <button className={mergedClasName} onClick={handleClick} ref={ref} {...props}>
          {/* Outer gradient container */}
          <div className={styles.buttonOuter}>
            {/* Inner gradient container */}
            <div className={styles.buttonInner}>
              {/* Sunflare ellipses */}
              <div className={styles.sunflare1} />
              <div className={styles.sunflare2} />

              {/* Content */}
              <div className={styles.buttonContent}>
                {loading ? (
                  <Spinner borderWidth={2} size={16} color="inherit" />
                ) : (
                  <span>{children}</span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    }

    // For other variants, use original implementation with centered content
    return (
      <button className={mergedClasName} onClick={handleClick} ref={ref} {...props}>
        <div className={styles.buttonContentWrapper}>
          {loading ? (
            <Spinner borderWidth={2} size={16} color="inherit" />
          ) : (
            <span>{children}</span>
          )}
        </div>
      </button>
    );
  },
);
